import {Fragment, useEffect, useState} from 'react';
import {Icon, Loader, Typography} from '@innowise-group/ui-kit';
import {useService} from '@innowise-group/core';
import {GeotrackAvatar} from '../geotrack-avatar';
import * as Styled from './geotrack-details-info.styles';
import {Table, TableData, TableRow} from '@innowise-group/ui-kit';
import exitIcon from '../../../../assets/icons/exit.svg';
import entranceIcon from '../../../../assets/icons/entrance.svg';
import {format} from 'date-fns';
import {EmployeeDateTimeMarksData} from '../../types';
import {useTranslation} from 'react-i18next';
import {withPlaceholder} from '@shared/utilities';
import {GeotrackApiService} from '../../services';
import {CenteredContainer} from '../../pages/geotrack-statuses/geotrack-statuses.styles';
import {formatTimeInTimeZone, timezoneForFaceidLocation} from '../../utilities';

const tableHeadList = [' ', 'pages.geotrack.filters.time', 'pages.geotrack.entryOffice'];

interface GeotrackDatailsInfoProps {
  initialValues: EmployeeDateTimeMarksData;
  onClose: () => void;
}

const GeotrackDatailsInfo: React.FC<GeotrackDatailsInfoProps> = ({initialValues, onClose}) => {
  const {t, i18n} = useTranslation();
  const geotrackApi = useService(GeotrackApiService);
  const [marks, setMarks] = useState(initialValues?.all_date_time_marks || []);
  const [isLoading, setIsLoading] = useState(!initialValues?.all_date_time_marks?.length);

  useEffect(() => {
    if (!initialValues?.id) {
      setIsLoading(false);
      return;
    }
    // Marks are no longer embedded in the list payload — load on modal open.
    if (initialValues.all_date_time_marks?.length) {
      setMarks(initialValues.all_date_time_marks);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const sub = geotrackApi.getGeotrackTimeMarks(initialValues.id).subscribe({
      next: (response) => {
        const localeIndex = +(i18n.language === 'ru');
        const normalized = (response.all_date_time_marks || []).map((dateTimeMark) => {
          const markLocation =
            (localeIndex ? dateTimeMark.location : dateTimeMark.location_eng || dateTimeMark.location) ||
            dateTimeMark.location;
          const markTimeZone =
            dateTimeMark.timezone || timezoneForFaceidLocation(markLocation || dateTimeMark.location);
          const city = localeIndex
            ? dateTimeMark.location_city
            : dateTimeMark.location_city_eng || dateTimeMark.location_city;
          return {
            ...dateTimeMark,
            location: markLocation,
            location_city: city,
            date_time: formatTimeInTimeZone(dateTimeMark?.date_time, markTimeZone),
          };
        });
        setMarks(normalized as typeof marks);
        setIsLoading(false);
      },
      error: () => {
        setMarks([]);
        setIsLoading(false);
      },
    });
    return () => sub.unsubscribe();
  }, [geotrackApi, initialValues, i18n.language]);

  return (
    <>
      <Styled.HeaderContainer>
        <Styled.HeaderWrapper>
          <GeotrackAvatar user={initialValues} avatarSize={54} />
          <div>
            <div>
              <Styled.EmployeeNameTypography>
                {initialValues?.preferred_name.map((part, i) => (
                  <Fragment key={part}>{part} </Fragment>
                ))}
              </Styled.EmployeeNameTypography>
            </div>
            <div>
              <Typography>{format(new Date(initialValues?.date), 'dd.MM.yyyy')}</Typography>
            </div>
          </div>
        </Styled.HeaderWrapper>
        <Icon type="u_multiply" onClick={onClose} isPointer isHover />
      </Styled.HeaderContainer>
      <Styled.GeotrackInfoContainer>
        {isLoading ? (
          <CenteredContainer>
            <Loader />
          </CenteredContainer>
        ) : (
          <Table>
            <thead>
              <TableRow>
                {tableHeadList.map((head, index) => (
                  <Styled.GeotrackTableHead key={head ?? index}>{head && t(head)}</Styled.GeotrackTableHead>
                ))}
              </TableRow>
            </thead>
            <tbody>
              {marks?.map((mark) => (
                <TableRow key={`${mark.date_time}-${mark.location}`}>
                  <TableData>
                    <img src={mark.is_entrance ? entranceIcon : exitIcon} alt="s" />
                  </TableData>
                  <TableData>
                    <Styled.GeotrackTypography noBreak>{withPlaceholder(mark.date_time)}</Styled.GeotrackTypography>
                  </TableData>
                  <TableData>
                    <Styled.GeotrackTypography noBreak>
                      {withPlaceholder(
                        mark?.location_country && mark.location && (
                          <>
                            <b>{[mark.location_city, mark.location_country].filter((it) => it).join(', ')}</b>
                            <br />
                            {mark.location}
                          </>
                        ),
                      )}
                    </Styled.GeotrackTypography>
                  </TableData>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </Styled.GeotrackInfoContainer>
    </>
  );
};

export default GeotrackDatailsInfo;
